import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/shared';

const ProvidersList = ({ providers, changeProvider, currentProvider, loadingChats, providerUnreadCounts = {}, providerLastMessageTime = {} }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProviders = providers.filter(
    provider => provider?.name?.toLowerCase().includes(searchTerm.toLowerCase()) && provider?.clientProvider === true
  );

  const sortedProviders = [...filteredProviders].sort((a, b) => {
    const aUnread = providerUnreadCounts[a.id] || 0;
    const bUnread = providerUnreadCounts[b.id] || 0;
    const aTime = providerLastMessageTime[a.id] || 0;
    const bTime = providerLastMessageTime[b.id] || 0;

    if (aUnread > 0 && bUnread === 0) return -1;
    if (aUnread === 0 && bUnread > 0) return 1;

    if (aUnread > 0 && bUnread > 0) {
      return bTime - aTime;
    }

    return a.name?.localeCompare(b.name || '') || 0;
  });

  return (
    <div className="w-full h-full border-r border-gray-200 bg-white relative flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('CHAT.selectProvider')}</h2>
        </div>

        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Icon name="search" className="text-gray-500" size="sm" />
          </div>
          <input
            type="search"
            className="w-full pl-10 p-2.5 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-feel-primary focus:border-feel-primary transition-colors"
            placeholder={t('CHAT.searchProviders')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loadingChats && (
          <div className="flex items-center justify-center p-8">
            <div role="status">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-feel-primary"></div>
              <span className="sr-only">{t('CHAT.loadingChats')}</span>
            </div>
          </div>
        )}

        {!loadingChats && (
          <div className="p-4 space-y-3">
            {sortedProviders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Icon name="users" size="lg" className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-500">{t('CHAT.noProviderFound')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('CHAT.tryAnotherSearch')}</p>
              </div>
            ) : (
              sortedProviders.map(provider => {
                const unreadCount = providerUnreadCounts[provider.id] || 0;
                const hasChatEnabled = provider?.b2bSubscription?.plan?.id >= 2;

                return (
                  <button
                    key={provider.id}
                    onClick={() => hasChatEnabled && changeProvider(provider)}
                    disabled={!hasChatEnabled}
                    className={`flex items-center justify-between gap-3 p-3 w-full rounded-lg transition-colors border ${
                      !hasChatEnabled
                        ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                        : 'hover:bg-gray-100 border-transparent hover:border-gray-200'
                    }`}
                  >
                    <div className="relative">
                      <img
                        className={`w-12 h-12 rounded-full object-cover shadow-sm ${
                          currentProvider?.id === provider.id ? 'ring-2 ring-feel-primary ring-offset-2' : 'ring-2 ring-gray-200'
                        }`}
                        src={provider?.images?.[0]?.small || '/icons/icon-512x512.png'}
                        alt=""
                      />
                      {currentProvider?.id === provider.id && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-feel-primary flex items-center justify-center shadow-lg">
                          <Icon name="check" className="text-white" size="xs" />
                        </div>
                      )}
                      {unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow-lg"></div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{provider.name}</h3>
                        {!hasChatEnabled ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-300 text-gray-600 font-medium whitespace-nowrap flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                            {t('CHAT.chatDisabled')}
                          </span>
                        ) : currentProvider?.id === provider.id ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-feel-primary text-white font-medium whitespace-nowrap">
                            {t('CHAT.active')}
                          </span>
                        ) : null}
                      </div>
                      {!hasChatEnabled && <p className="text-xs text-gray-500 mt-1 italic">{t('CHAT.chatNotAvailable')}</p>}
                      {hasChatEnabled && provider.description && <p className="text-xs text-gray-500 truncate mt-1">{provider.description}</p>}
                      {hasChatEnabled && provider.address && (
                        <div className="flex items-center gap-1.5 mt-2">
                          <Icon name="location-marker" className="text-gray-400 flex-shrink-0" size="xs" />
                          <span className="text-xs text-gray-500 truncate">{provider.address}</span>
                        </div>
                      )}
                    </div>

                    <Icon name="chevron-right" className="text-gray-400 flex-shrink-0" size="md" />
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvidersList;
